import React, { useEffect, useState } from "react";
import { HiOutlineCog, HiOutlineEye, HiOutlineCheck, HiOutlineX, HiOutlineClock, HiOutlineCalendar, HiOutlineUserGroup, HiOutlineClipboardList, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlinePlay, HiOutlinePause, HiOutlineExclamation, HiOutlineCurrencyDollar } from 'react-icons/hi';

export default function UretimPage() {
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [productionProcesses, setProductionProcesses] = useState([]);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("details"); // details, processes

  // Satışları yükle
  useEffect(() => {
    fetch('/api/sales')
      .then(res => res.json())
      .then(data => setSales(data))
      .catch(() => setSales([]));
  }, []);

  // Satış detaylarını yükle
  const loadSaleDetails = async (saleId) => {
    try {
      const resp = await fetch(`/api/sales/${saleId}`);
      const saleData = await resp.json();
      setSelectedSale(saleData);
    } catch (err) {
      console.error('Satış detayları yüklenemedi:', err);
    }
  };

  // Üretim süreçlerini yükle
  const loadProductionProcesses = async (saleItemId) => {
    try {
      const resp = await fetch(`/api/production/${saleItemId}`);
      const data = await resp.json();
      setProductionProcesses(data);
    } catch (err) {
      console.error('Üretim süreçleri yüklenemedi:', err);
      setProductionProcesses([]);
    }
  };

  // Üretim süreci güncelle
  const handleProcessUpdate = async (processId, updates) => {
    try {
      const resp = await fetch(`/api/production/${processId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!resp.ok) throw new Error('Süreç güncellenemedi');
      
      // Süreçleri yeniden yükle
      if (selectedItem) {
        loadProductionProcesses(selectedItem.id);
      }
    } catch (err) {
      alert('Süreç güncellenemedi: ' + err.message);
    }
  };

  // Üretim durumu güncelle
  const handleProductionStatusUpdate = async (itemId, newStatus, notes) => {
    try {
      const resp = await fetch(`/api/sales/items/${itemId}/production-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ production_status: newStatus, production_notes: notes })
      });
      if (!resp.ok) throw new Error('Durum güncellenemedi');
      
      // Satışları yeniden yükle
      fetch('/api/sales')
        .then(res => res.json())
        .then(data => setSales(data));
    } catch (err) {
      alert('Durum güncellenemedi: ' + err.message);
    }
  };

  // Filtreleme
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.sale_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sale.partner_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || sale.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'ordered': return 'bg-blue-100 text-blue-800';
      case 'in_production': return 'bg-yellow-100 text-yellow-800';
      case 'ready_for_delivery': return 'bg-green-100 text-green-800';
      case 'delivered': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ordered': return 'Sipariş Verildi';
      case 'in_production': return 'Üretimde';
      case 'ready_for_delivery': return 'Teslime Hazır';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      default: return 'Bilinmiyor';
    }
  };

  const getProductionStatusColor = (status) => {
    switch (status) {
      case 'not_started': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'quality_check': return 'bg-blue-100 text-blue-800';
      case 'ready': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProductionStatusText = (status) => {
    switch (status) {
      case 'not_started': return 'Siparişiniz İşleniyor';
      case 'in_progress': return 'Üretimde';
      case 'completed': return 'Tamamlandı';
      case 'quality_check': return 'Kalite Kontrol';
      case 'ready': return 'Hazır';
      default: return 'Bilinmiyor';
    }
  };

  const getProcessStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProcessStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'in_progress': return 'Devam Ediyor';
      case 'completed': return 'Tamamlandı';
      case 'failed': return 'Başarısız';
      default: return 'Bilinmiyor';
    }
  };

  const getProcessStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <HiOutlineClock className="w-4 h-4" />;
      case 'in_progress': return <HiOutlinePlay className="w-4 h-4" />;
      case 'completed': return <HiOutlineCheckCircle className="w-4 h-4" />;
      case 'failed': return <HiOutlineXCircle className="w-4 h-4" />;
      default: return <HiOutlineExclamation className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3 px-6 pt-6">
          <HiOutlineCog className="w-8 h-8 text-blue-700" />
          Üretim Yönetimi
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol Panel - Satış Listesi */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-blue-900">Satışlar</h2>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="ordered">Sipariş Verildi</option>
                    <option value="in_production">Üretimde</option>
                    <option value="ready_for_delivery">Teslime Hazır</option>
                    <option value="delivered">Teslim Edildi</option>
                    <option value="cancelled">İptal Edildi</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredSales.map(sale => (
                  <div
                    key={sale.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedSale?.id === sale.id
                        ? 'bg-blue-50 border-2 border-blue-300'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => loadSaleDetails(sale.id)}
                  >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-bold">
                        {String(sale.id).padStart(2, '0')}
                      </span>
                      <div>
                        <h3 className="font-semibold text-sm">Üretim Emri #{String(sale.id).padStart(4, '0')}</h3>
                        <p className="text-xs text-gray-600">Sipariş Sahibi: Gizli</p>
                      </div>
                    </div>
                      <div className="flex items-center gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(sale.status)}`}>
                          {getStatusText(sale.status)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <HiOutlineCalendar className="w-3 h-3" />
                      {new Date(sale.order_date).toLocaleDateString('tr-TR')}
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <HiOutlineCurrencyDollar className="w-3 h-3" />
                      {sale.total_amount?.toLocaleString('tr-TR')} {sale.currency}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sağ Panel - Üretim Detayları */}
          <div className="lg:col-span-2">
            {selectedSale ? (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-blue-900">Üretim Detayları</h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(selectedSale.status)}`}>
                    {getStatusText(selectedSale.status)}
                  </span>
                </div>

                {/* Satış Bilgileri */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
                  <div><b>Sipariş Sahibi:</b> Gizli</div>
                  <div><b>Sipariş Tarihi:</b> {new Date(selectedSale.order_date).toLocaleDateString('tr-TR')}</div>
                  <div><b>Teslimat Tarihi:</b> {selectedSale.delivery_date ? new Date(selectedSale.delivery_date).toLocaleDateString('tr-TR') : '-'}</div>
                  <div><b>Toplam Tutar:</b> {selectedSale.total_amount?.toLocaleString('tr-TR')} {selectedSale.currency}</div>
                </div>

                {/* Ürün Listesi */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-blue-900">Üretim Ürünleri</h3>
                  {selectedSale.items && selectedSale.items.length > 0 ? (
                    selectedSale.items.map(item => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold">{item.product_name}</h4>
                            <p className="text-sm text-gray-600">{item.product_code} - {item.quantity} adet</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getProductionStatusColor(item.production_status)}`}>
                              {getProductionStatusText(item.production_status)}
                            </span>
                            <button
                              className="px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-semibold"
                              onClick={() => {
                                setSelectedItem(item);
                                loadProductionProcesses(item.id);
                                setActiveTab("details");
                              }}
                            >
                              <HiOutlineEye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Üretim Durumu Güncelleme */}
                        <div className="flex gap-2">
                          <select
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                            value={item.production_status}
                            onChange={(e) => handleProductionStatusUpdate(item.id, e.target.value, item.production_notes)}
                          >
                            <option value="not_started">Başlamadı</option>
                            <option value="in_progress">Üretimde</option>
                            <option value="completed">Tamamlandı</option>
                            <option value="quality_check">Kalite Kontrol</option>
                            <option value="ready">Hazır</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Üretim notları..."
                            className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
                            value={item.production_notes || ''}
                            onChange={(e) => handleProductionStatusUpdate(item.id, item.production_status, e.target.value)}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Bu satış için ürün bulunamadı</p>
                    </div>
                  )}
                </div>

                {/* Seçili Ürün Detayları ve Sekmeler */}
                {selectedItem && (
                  <div className="mt-6 bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">
                        {selectedItem.product_name} - {selectedItem.quantity} adet
                      </h3>
                      <p className="text-sm text-gray-600">{selectedItem.product_code}</p>
                    </div>

                    {/* Sekme Başlıkları */}
                    <div className="flex border-b border-gray-200 mb-4">
                      <button
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                          activeTab === "details"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => setActiveTab("details")}
                      >
                        Ürün Detayları
                      </button>
                      <button
                        className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                          activeTab === "processes"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => setActiveTab("processes")}
                      >
                        Üretim Süreçleri ({productionProcesses.length})
                      </button>
                    </div>

                    {/* Sekme İçerikleri */}
                    {activeTab === "details" && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Ürün Adı</label>
                            <p className="text-sm text-gray-900">{selectedItem.product_name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Ürün Kodu</label>
                            <p className="text-sm text-gray-900">{selectedItem.product_code}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Miktar</label>
                            <p className="text-sm text-gray-900">{selectedItem.quantity} adet</p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Birim Fiyat</label>
                            <p className="text-sm text-gray-900">{Number(selectedItem.unit_price).toLocaleString('tr-TR')} ₺</p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Toplam Fiyat</label>
                            <p className="text-sm text-gray-900">{Number(selectedItem.total_price).toLocaleString('tr-TR')} ₺</p>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Üretim Durumu</label>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getProductionStatusColor(selectedItem.production_status)}`}>
                              {getProductionStatusText(selectedItem.production_status)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "processes" && (
                      <div className="space-y-4">
                        {productionProcesses.length > 0 ? (
                          productionProcesses.map(process => (
                            <div key={process.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold">{process.process_name}</h4>
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getProcessStatusColor(process.status)}`}>
                                  {getProcessStatusIcon(process.status)}
                                  {getProcessStatusText(process.status)}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <label className="block text-sm font-semibold text-blue-900 mb-1">Başlangıç Tarihi</label>
                                  <input
                                    type="datetime-local"
                                    className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
                                    value={process.start_date ? new Date(process.start_date).toISOString().slice(0, 16) : ''}
                                    onChange={(e) => handleProcessUpdate(process.id, { start_date: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-blue-900 mb-1">Bitiş Tarihi</label>
                                  <input
                                    type="datetime-local"
                                    className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
                                    value={process.end_date ? new Date(process.end_date).toISOString().slice(0, 16) : ''}
                                    onChange={(e) => handleProcessUpdate(process.id, { end_date: e.target.value })}
                                  />
                                </div>
                              </div>
                              
                              <div className="mt-3">
                                <label className="block text-sm font-semibold text-blue-900 mb-1">Durum</label>
                                <select
                                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
                                  value={process.status}
                                  onChange={(e) => handleProcessUpdate(process.id, { status: e.target.value })}
                                >
                                  <option value="pending">Bekliyor</option>
                                  <option value="in_progress">Devam Ediyor</option>
                                  <option value="completed">Tamamlandı</option>
                                  <option value="failed">Başarısız</option>
                                </select>
                              </div>
                              
                              <div className="mt-3">
                                <label className="block text-sm font-semibold text-blue-900 mb-1">Notlar</label>
                                <textarea
                                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
                                  rows="2"
                                  value={process.notes || ''}
                                  onChange={(e) => handleProcessUpdate(process.id, { notes: e.target.value })}
                                  placeholder="Süreç notları..."
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>Bu ürün için üretim süreci bulunamadı</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <HiOutlineCog className="w-16 h-16 mx-auto mb-4" />
                  <p>Üretim detaylarını görmek için bir satış seçin</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 